import { useEffect, useRef } from 'react';

type UseStickyTabIndexOptions = {

    // Id of the entity currently open (classroom, access code, student, etc.).
    // The remembered tab is scoped to this id. Null/undefined disables persistence.
    entityId: string | number | null | undefined;

    // Called with a remembered tab index when the opened entity changes and a
    // value was previously stored for it. Apply it to your own tab state.
    onRestore: (index: number) => void;

    // Stable namespace for the tab group, e.g. 'classroom', 'accessCode', 'student'.
    scope: string;

    // The currently active tab index. Persisted whenever it changes.
    value: number;
};

/**
 * Persists the active tab index for a specific entity (classroom, access code,
 * student, transaction, etc.) so it survives a page refresh, without owning the
 * tab state itself.
 *
 * It layers on top of whatever tab state the caller already has: pass the current
 * `value` and an `onRestore` callback. The hook writes `value` to `sessionStorage`
 * (keyed by `scope` + `entityId`) whenever it changes, and calls `onRestore` with
 * the remembered index when the opened entity changes so the caller can apply it.
 *
 * The memory is scoped to the entity: opening a different entity restores that
 * entity's own remembered tab rather than leaking the previous one, and because it
 * lives in `sessionStorage` it clears naturally when the browser tab is closed.
 */
export const useStickyTabIndex = (options: UseStickyTabIndexOptions): void => {

    const { entityId, onRestore, scope, value } = options;

    const storageKey = entityId !== null && entityId !== undefined
        ? `stickyTab:${scope}:${entityId}`
        : null;

    // Keep the latest onRestore without making it an effect dependency.
    const onRestoreRef = useRef(onRestore);
    onRestoreRef.current = onRestore;

    // Tracks the entity we last restored, so a key change restores while a value
    // change (for the same entity) persists — never both in the same commit.
    const lastKeyRef = useRef<string | null>(null);

    useEffect(() => {

        if (!storageKey) {

            return;

        }

        if (lastKeyRef.current !== storageKey) {

            // Entity just opened (or changed): restore its remembered tab, don't persist.
            lastKeyRef.current = storageKey;
            try {

                const raw = sessionStorage.getItem(storageKey);
                if (raw !== null) {

                    const parsed = Number(raw);
                    if (Number.isInteger(parsed) && parsed >= 0) {

                        onRestoreRef.current(parsed);

                    }

                }

            } catch {

                // Ignore storage failures (private mode, quota, etc.).

            }
            return;

        }

        // Same entity, active tab changed: persist it.
        if (Number.isInteger(value)) {

            try {

                sessionStorage.setItem(storageKey, String(value));

            } catch {

                // Ignore storage failures (private mode, quota, etc.).

            }

        }

    }, [storageKey, value]);

};
